import React, { useState } from 'react';
import { ChevronDown, ChevronRight, School, Users, BookOpen, Building, FileText, Award, Mail, Phone, MapPin, Eye } from 'lucide-react';

interface GeneralInfo {
  sno: number;
  information: string;
  details: string;
}

interface Document {
  sno: number;
  document: string;
  hasView: boolean;
}

interface StaffInfo {
  sno: number;
  information: string;
  details: string | number;
}

interface InfrastructureInfo {
  sno: number;
  information: string;
  details: string | number;
}

const SchoolDisclosure: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('general');

  const generalInfo: GeneralInfo[] = [
    { sno: 1, information: "Name of School", details: "Ever Green Public School" },
    { sno: 2, information: "Affiliation No.", details: "3530296" },
    { sno: 3, information: "School Code", details: "81522" },
    { sno: 4, information: "Complete Address", details: "Sri Ganga Vihar Colony, Beriparao, P.O. - Motahaldu, Haldwani, Nainital, U.K. (263139)" },
    { sno: 5, information: "Principal Name & Qualification", details: "Mr. M.S. Parwal & M.Sc, B.Ed" },
    { sno: 6, information: "Email ID", details: "evergreenpublicschool42@yahoo.in" },
    { sno: 7, information: "Contact Details", details: "(05946) 292491, 9997182574, 8958851161" }
  ];

  const documents: Document[] = [
    { sno: 1, document: "Copies of Affiliation/Upgradation Letter and Recent Extension of Affiliation, if any", hasView: true },
    { sno: 2, document: "Copies of Societies/Trust/Company Registration/Renewal Certificate, as applicable", hasView: true },
    { sno: 3, document: "Copy of No Objection Certificate (NOC) Issued, if applicable, by the State Govt./UT", hasView: true },
    { sno: 4, document: "Copies of Recognition Certificate Under RTE Act, 2009 and its Renewal if applicable", hasView: true },
    { sno: 5, document: "Copy of Valid Building Safety Certificate as per the National Building Code", hasView: true },
    { sno: 6, document: "Copy of Valid Fire Safety Certificate Issued by the Competent Authority", hasView: true },
    { sno: 7, document: "Copy of The DEO Certificate Submitted by the School for Affiliation/Upgradation/Extension", hasView: true },
    { sno: 8, document: "Copies of Valid Water, Health And Sanitation Certificates", hasView: true },
    { sno: 9, document: "Copies of Land Certificates", hasView: true },
    { sno: 10, document: "COPY OF MANDATORY INFORMATION AS PER CBSE CIRCULAR", hasView: true }
  ];

  const resultsAcademics: Document[] = [
    { sno: 1, document: "Fee Structure of School", hasView: true },
    { sno: 2, document: "Annual Academic Calendar", hasView: true },
    { sno: 3, document: "List of School Management Committee (SMC)", hasView: true },
    { sno: 4, document: "List of Parents Teachers Association (PTA) Members", hasView: true },
    { sno: 5, document: "Last Three-Year Result of The Board Examination as per Applicability", hasView: true }
  ];

  const staffInfo: StaffInfo[] = [
    { sno: 1, information: "Principal", details: "01" },
    { sno: 2, information: "Total No. of Teachers", details: "54" },
    { sno: 3, information: "PGT", details: "18" },
    { sno: 4, information: "TGT", details: "16" },
    { sno: 5, information: "PRT", details: "16" },
    { sno: 6, information: "NTT", details: "04" },
    { sno: 7, information: "Teachers Section Ratio", details: "1:1.5" },
    { sno: 8, information: "Details of Special Educator", details: "Mrs Pooja Joshi (M.A, B.Ed.) RCI No. - A86200" },
    { sno: 9, information: "Details of Counsellor and Wellness Teacher", details: "Mrs. Poonam Pathak (M.A, B.Ed.)" }
  ];

  const infrastructureInfo: InfrastructureInfo[] = [
    { sno: 1, information: "Total Campus Area of The School (In Square Mtr)", details: "8093.712" },
    { sno: 2, information: "No. and Size of The Class Rooms (In Sq Ft mtr)", details: "43 (37 - 20X20, 06 - 20X25)" },
    { sno: 3, information: "No. and Size of Laboratories Including Computer Labs (In Sq Mtr)", details: "06 (02 - 7X12, 03 - 6X9, 01 - 6X10)" },
    { sno: 4, information: "Internet Facility", details: "Yes" },
    { sno: 5, information: "No. of Girls Toilets", details: "20" },
    { sno: 6, information: "No. of Boys Toilets", details: "7 with Door, 21 Urinals" },
    { sno: 7, information: "Link of Youtube Video of the Inspection of School covering the Infrastructure", details: "Youtube Link" }
  ];

  const sections = [
    { id: 'general', title: 'General Information', icon: School, color: 'bg-blue-500' },
    { id: 'documents', title: 'Documents & Information', icon: FileText, color: 'bg-green-500' },
    { id: 'academics', title: 'Result & Academics', icon: Award, color: 'bg-purple-500' },
    { id: 'staff', title: 'Staff (Teaching)', icon: Users, color: 'bg-orange-500' },
    { id: 'infrastructure', title: 'School Infrastructure', icon: Building, color: 'bg-red-500' }
  ];

  const renderInfoTable = (data: GeneralInfo[] | StaffInfo[] | InfrastructureInfo[]) => (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse bg-white rounded-lg shadow-sm">
        <thead>
          <tr className="bg-gray-50">
            <th className="border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-700 w-16">S. No.</th>
            <th className="border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-700">Information</th>
            <th className="border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-700">Details</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={index} className="hover:bg-gray-50 transition-colors">
              <td className="border border-gray-200 px-4 py-3 text-sm text-gray-600 font-medium">{item.sno}</td>
              <td className="border border-gray-200 px-4 py-3 text-sm text-gray-700">{item.information}</td>
              <td className="border border-gray-200 px-4 py-3 text-sm text-gray-600">
                {item.information.includes('Email') ? (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-blue-500" />
                    <a href={`mailto:${item.details}`} className="text-blue-600 hover:underline">{item.details}</a>
                  </div>
                ) : item.information.includes('Contact') ? (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-green-500" />
                    <span>{item.details}</span>
                  </div>
                ) : item.information.includes('Address') ? (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <span>{item.details}</span>
                  </div>
                ) : (
                  item.details
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderDocumentTable = (data: Document[], title: string) => (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse bg-white rounded-lg shadow-sm">
        <thead>
          <tr className="bg-gray-50">
            <th className="border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-700 w-16">S. No.</th>
            <th className="border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-700">
              {title === 'Documents & Information' ? 'Documents & Information' : 'Documents & Information'}
            </th>
            <th className="border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-700 w-24">Documents</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={index} className="hover:bg-gray-50 transition-colors">
              <td className="border border-gray-200 px-4 py-3 text-sm text-gray-600 font-medium">{item.sno}</td>
              <td className="border border-gray-200 px-4 py-3 text-sm text-gray-700">{item.document}</td>
              <td className="border border-gray-200 px-4 py-3 text-sm">
                {item.hasView && (
                  <button className="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md text-xs font-medium transition-colors">
                    <Eye className="w-3 h-3" />
                    View
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg mb-8 p-8 text-white">
        <div className="flex items-center gap-4 mb-4">
          <div className="bg-white/20 p-3 rounded-full">
            <School className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Ever Green Public School</h1>
            <p className="text-blue-100 mt-1">Mandatory Public Disclosure</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 text-sm">
          <div className="bg-white/10 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-4 h-4" />
              <span className="font-semibold">Location</span>
            </div>
            <p className="text-blue-100">Haldwani, Nainital, Uttarakhand</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-4 h-4" />
              <span className="font-semibold">Affiliation</span>
            </div>
            <p className="text-blue-100">CBSE - 3530296</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4" />
              <span className="font-semibold">Total Teachers</span>
            </div>
            <p className="text-blue-100">54 Teaching Staff</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex flex-wrap gap-2 mb-8">
        {sections.map((section) => {
          const IconComponent = section.icon;
          return (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                activeSection === section.id
                  ? `${section.color} text-white shadow-lg`
                  : 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm'
              }`}
            >
              <IconComponent className="w-4 h-4" />
              {section.title}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        {activeSection === 'general' && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-blue-500 p-2 rounded-lg">
                <School className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">General Information</h2>
            </div>
            {renderInfoTable(generalInfo)}
          </div>
        )}

        {activeSection === 'documents' && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-green-500 p-2 rounded-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Documents & Information</h2>
            </div>
            {renderDocumentTable(documents, 'Documents & Information')}
          </div>
        )}

        {activeSection === 'academics' && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-purple-500 p-2 rounded-lg">
                <Award className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Result & Academics</h2>
            </div>
            {renderDocumentTable(resultsAcademics, 'Result & Academics')}
          </div>
        )}

        {activeSection === 'staff' && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-orange-500 p-2 rounded-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Staff (Teaching)</h2>
            </div>
            {renderInfoTable(staffInfo)}
          </div>
        )}

        {activeSection === 'infrastructure' && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-red-500 p-2 rounded-lg">
                <Building className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">School Infrastructure</h2>
            </div>
            {renderInfoTable(infrastructureInfo)}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-gray-600 text-sm">
        <p className="bg-white rounded-lg p-4 shadow-sm">
          This mandatory disclosure is published in compliance with CBSE guidelines and regulations.
          <br />
          Last updated: June 2025
        </p>
      </div>
    </div>
  );
};

export default SchoolDisclosure;